import { RecordFile } from '../types';
import React, { useState } from 'react';
import { message, Upload, UploadFile } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axiosInstance';
import FilePreviewModal from './FilePreviewModal';

interface AttachmentUploadProps {
  attachments?: RecordFile[];
  /**
   * Module base path, e.g. 'partner/vendors'. Supply together with recordId to
   * allow adding and removing; omit either and the list stays read-only, which
   * is how the detail pages have always used this.
   */
  api?: string;
  recordId?: string;
  /** Groups the file, e.g. 'accreditation'. Sent through to the server. */
  tag?: string;
  /** Respects the caller's module permission; read-only when false. */
  canEdit?: boolean;
  label?: string;
}

/** Matches spring.servlet.multipart.max-file-size on the server. */
const MAX_MB = 10;

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({
  attachments,
  api,
  recordId,
  tag = '',
  canEdit = false,
  label = 'Attachments',
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null);
  // A counter, not a boolean: antd calls customRequest once per selected file,
  // so a batch has several uploads in flight. With a boolean the first one to
  // finish would re-enable the control while the rest were still going.
  const [inFlight, setInFlight] = useState(0);
  const queryClient = useQueryClient();

  // Editable only when we know where to put the file and the user may write.
  const editable = Boolean(api && recordId && canEdit);

  const handleCancelPreview = () => setPreviewOpen(false);
  const handlePreview = (file: UploadFile) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [`${api}/${recordId}/files`] });

  const beforeUpload = (file: File) => {
    if (file.size > MAX_MB * 1024 * 1024) {
      message.error(`${file.name} is larger than ${MAX_MB}MB and was not added.`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  // customRequest so the file goes to the module's own upload endpoint with the
  // auth header the axios instance attaches, rather than antd's bare XHR.
  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const form = new FormData();
    form.append('files', file);
    form.append('tag', tag);
    setInFlight((n) => n + 1);
    let remaining = 0;
    try {
      await axiosInstance.put(`${api}/${recordId}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess?.({}, file);
      message.success(`${file.name} uploaded`);
    } catch (e) {
      onError?.(e as Error);
      message.error(`${file.name} could not be uploaded. Please try again.`);
    } finally {
      setInFlight((n) => {
        remaining = n - 1;
        return remaining;
      });
      // Refresh once the whole batch has settled rather than after each file.
      if (remaining === 0) {
        await refresh();
      }
    }
  };

  const handleRemove = async (file: UploadFile) => {
    // useFetchFile rewrites `code` into a full URL for display, so the raw code
    // the API expects is taken from the last path segment.
    const code = String(file.url ?? '').split('/').pop();
    if (!code) return false;
    try {
      await axiosInstance.delete(`${api}/files`, { params: { ids: code } });
      message.success(`${file.name} removed`);
      await refresh();
      return true;
    } catch (e) {
      message.error(`${file.name} could not be removed.`);
      return false;
    }
  };

  const fileList: UploadFile[] = (attachments ?? []).map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  return (
    <>
      <span>{label}</span>
      <Upload
        showUploadList={{
          showDownloadIcon: true,
          showRemoveIcon: editable,
        }}
        listType={'picture-card'}
        fileList={fileList}
        disabled={!editable || inFlight > 0}
        onPreview={handlePreview}
        beforeUpload={editable ? beforeUpload : undefined}
        customRequest={editable ? customRequest : undefined}
        onRemove={editable ? handleRemove : undefined}
        multiple
      >
        {editable && (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Add file</div>
          </div>
        )}
      </Upload>
      <FilePreviewModal
        visible={previewOpen}
        onCancel={handleCancelPreview}
        file={previewFile as UploadFile}
      />
    </>
  );
};

export default AttachmentUpload;
